"use client";

import { useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Knopf } from "../knopf";
import { SchliessenSymbol, useEingehaengt } from "./kleines-modal";
import { useModalVerhalten } from "./modal-verhalten";

/*
 * Seitenpanel (Offcanvas rechts, Design-Guide `seiten-panel.md`): ein Formular neben der Seite öffnen, ohne sie zu verlassen (z. B.
 * „+ News“ auf einer Bereichsseite). Kopf mit Titel und Schließen, Inhalt mit eigener Scrollspalte, volle Höhe.
 *
 * Abweichung AW-125 vom Block `create-event-drawer-with-schedule-form` (Overlay rechts): Breite `sm:max-w-2xl` statt `sm:w-100` (das
 * Formular des Beitrag- bzw. Event-Editors braucht Platz), React-gesteuert statt `hs-overlay` (wie AW-008), ohne Einschiebe-Animation,
 * Hintergrund abgedunkelt wie die Modale (Fokusfalle, Klick daneben schließt).
 *
 * Verhalten (per Test belegt): `role="dialog"`, `aria-modal`, `aria-labelledby`, Esc schließt, Fokusfalle, Fokus kehrt zum Auslöser zurück.
 */
export function SeitenPanel(props: {
  offen: boolean;
  titel: string;
  beimSchliessen: () => void;
  /**
   * Aktion im Kopf neben dem Schließen (additiv, Abweichung AW-142, Rechte-Matrix R3, Betreiber 20.10.2026: „Als Seite öffnen“ im Rollen-Detail). Nur eine
   * ruhige Aktion in Knopfgestalt (Aufrufer prüft das Recht, r110); das Panel selbst prüft nichts.
   */
  kopfAktion?: ReactNode;
  /**
   * Breite ab `sm` (additiv, Abweichung AW-145, Zugang aus HRWorks, Betreiber 21.10.2026 "V1"): `standard` = `max-w-2xl` (672 px, unverändert),
   * `breit` = 780 px (`max-w-195`) für zwei Spalten nebeneinander (HRWorks | Konto). Unter `sm` immer Vollfläche.
   */
  breite?: "standard" | "breit";
  /**
   * Fester Fuß am unteren Rand (additiv, entspricht der Preline-Vorschau (M-122)): Aktionsknöpfe stehen
   * immer im Bild, der Inhalt scrollt darüber. Ohne die Prop kein Fuß, bisherige Verbraucher unverändert. Das Panel prüft nichts: was im Fuß steht, prüft der Aufrufer (r110).
   */
  fuss?: ReactNode;
  children: ReactNode;
}) {
  const eingehaengt = useEingehaengt();
  if (!props.offen || !eingehaengt) return null;
  return createPortal(<Inhalt {...props} />, document.body);
}

// Feste, vollständige Klassenketten je Breite (nie zusammengesetzt).
const HUELLE_STANDARD = "fixed top-0 inset-e-0 z-80 flex flex-col size-full sm:max-w-2xl bg-overlay border-s border-overlay-line";
const HUELLE_BREIT = "fixed top-0 inset-e-0 z-80 flex flex-col size-full sm:max-w-195 bg-overlay border-s border-overlay-line";

function Inhalt({ titel, beimSchliessen, kopfAktion, breite = "standard", fuss, children }: { titel: string; beimSchliessen: () => void; kopfAktion?: ReactNode; breite?: "standard" | "breit"; fuss?: ReactNode; children: ReactNode }) {
  const huelle = useRef<HTMLDivElement>(null);
  const id = useId();
  useModalVerhalten({ offen: true, huelle, beimSchliessen });
  return (
    <>
      {/* Preline: productivity/productivity-sub-calendars-scheduling/productivity-cat-calendar-drawers/create-event-drawer-with-schedule-form */}
      {/* Abweichung AW-125: Breite, React-gesteuert, ohne Einschiebe-Animation, abgedunkelter Hintergrund */}
      <div className="fixed inset-0 z-70 bg-foreground/50" aria-hidden="true" onClick={beimSchliessen} />
      <div ref={huelle} className={breite === "breit" ? HUELLE_BREIT : HUELLE_STANDARD} role="dialog" aria-modal="true" aria-labelledby={`${id}-titel`} tabIndex={-1}>
        <div className="py-2.5 px-4 flex justify-between items-center border-b border-overlay-divider">
          <h2 id={`${id}-titel`} className="font-medium text-foreground">
            {titel}
          </h2>
          <div className="flex items-center gap-x-2">
            {kopfAktion}
            <Knopf variante="ghost" nurSymbol groesse="sm" label="Schließen (Esc)" symbol={<SchliessenSymbol />} onClick={beimSchliessen} />
          </div>
        </div>
        <div className="grow min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb">
          <div className="p-4">{children}</div>
        </div>
        {fuss ? (
          <div data-seiten-panel-fuss className="shrink-0 py-3 px-4 flex flex-wrap items-start justify-end gap-2 border-t border-overlay-divider">
            {fuss}
          </div>
        ) : null}
      </div>
    </>
  );
}
